import React, { useState, useEffect, useCallback } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	TextField,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	Checkbox,
	Button,
	Box,
	Pagination,
	Stack,
	Grid,
	InputAdornment,
	TableSortLabel,
	IconButton,
	Paper,
	Tooltip,
	Switch,
	FormControlLabel,
	Alert,
} from "@mui/material";
import zIndex from "../../designTokens/zIndex";
import { useTranslation } from "react-i18next";
import { IconSearch } from "@tabler/icons-react";
import { IconPlus } from "@tabler/icons-react";
import { IconPencilCheck } from "@tabler/icons-react";
import { IconX } from "@tabler/icons-react";
import { IconLock } from "@tabler/icons-react";
import { IconWorld } from "@tabler/icons-react";
import { useUser } from "../../contexts/UserContext";
import { CardPost } from "../Card/CardPost";
import LabeledTextInput from "../PostThreads/LabeledTextInput";
import LabeledTextarea from "../PostThreads/LabeledTextarea";

const UpdateCollectionModal = ({
	modalOpen,
	setModalOpen,
	title,
	setTitle,
	description,
	setDescription,
	threadsList,
	selectedThreads,
	handleThreadSelect,
	isMobile,
	collectionList,
	onUpdateCollection,
	handleRemoveThread,
	isNew = true,
	initialIsPublic = true,
	overrideUserProfile = null,
}) => {
	const { t } = useTranslation();
	const user = useUser();
	const [searchQuery, setSearchQuery] = useState("");
	const [filteredThreads, setFilteredThreads] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [isPublic, setIsPublic] = useState(initialIsPublic);

    // Initialize/reset visibility state from the provided initial value when modal opens or value changes
    useEffect(() => {
        if (!modalOpen) return;
        setIsPublic(Boolean(initialIsPublic));
    }, [modalOpen, initialIsPublic]);
	const threadsPerPage = 10; // Adjust the number of threads per page as needed

	const [sortConfig, setSortConfig] = useState({
		key: "date",
		direction: "desc",
	});

    const sortThreads = useCallback((threads) => {
		if (!sortConfig.key) return threads;

		return [...threads].sort((a, b) => {
			if (sortConfig.key === "date") {
				const dateA = new Date(a.timestamp);
				const dateB = new Date(b.timestamp);
				return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
			}
			if (
				sortConfig.key === "engagementRate" ||
				sortConfig.key === "engagementAmount"
			) {
				const valA =
					sortConfig.key === "engagementRate"
						? ((a.likes + a.replies + a.reposts) / a.views) * 100
						: a.likes + a.replies + a.reposts;
				const valB =
					sortConfig.key === "engagementRate"
						? ((b.likes + b.replies + b.reposts) / b.views) * 100
						: b.likes + b.replies + b.reposts;
				return sortConfig.direction === "asc" ? valA - valB : valB - valA;
			}
			if (sortConfig.direction === "asc") {
				return a[sortConfig.key] - b[sortConfig.key];
			}
			return b[sortConfig.key] - a[sortConfig.key];
		});
    }, [sortConfig]);

	const handleSort = (key) => {
		setSortConfig((current) => ({
			key,
			direction:
				current.key === key && current.direction === "asc" ? "desc" : "asc",
		}));
	};

    useEffect(() => {
		const filtered = threadsList.filter(
			(thread) =>
				thread.text &&
				thread.text.toLowerCase().includes(searchQuery.toLowerCase())
		);
		const sorted = sortThreads(filtered);
		setFilteredThreads(sorted);
		setCurrentPage(1);
    }, [searchQuery, threadsList, sortThreads]);

	const indexOfLastThread = currentPage * threadsPerPage;
	const indexOfFirstThread = indexOfLastThread - threadsPerPage;
	const currentThreads = filteredThreads.slice(
		indexOfFirstThread,
		indexOfLastThread
	);

	const columns = [
		{ key: "views", label: "Views", mobileHidden: true },
		{ key: "likes", label: "Likes", mobileHidden: true },
		{ key: "replies", label: "Replies", mobileHidden: true },
		{ key: "reposts", label: "Reposts", mobileHidden: true },
	];

	return (
		<Dialog
			maxWidth={false}
			fullScreen={false}
			open={modalOpen}
			onClose={() => setModalOpen(false)}
			slotProps={{
				backdrop: {
					style: {
						backdropFilter: "blur(8px)",
						backgroundColor: "rgba(0, 0, 0, 0.5)"
					}
				}
			}}
			PaperProps={{
				style: {
					width: isMobile ? "calc(100vw - 32px)" : "calc(100vw - 96px)",
					height: isMobile ? "calc(100vh - 32px)" : "calc(100vh - 96px)",
					maxWidth: "none",
					maxHeight: "none",
					margin: isMobile ? "16px" : "48px",
					borderRadius: "16px",
					zIndex: zIndex.modal,
				},
			}}
		>
			<DialogTitle 
				sx={{ 
					padding: isMobile ? "32px 32px 24px 32px" : "64px 64px 32px 64px", 
					fontSize: "24px", 
					fontWeight: 600,
					position: "relative"
				}}
			>
				{t("myCollection.create")}
				<IconButton
					onClick={() => setModalOpen(false)}
					sx={{
						position: "absolute",
						top: isMobile ? "24px" : "48px",
						right: isMobile ? "24px" : "48px",
						padding: "8px",
						backgroundColor: "rgba(0, 0, 0, 0.04)",
						borderRadius: "8px"
					}}
					size="medium"
				>
					<IconX size={20} />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ 
				padding: isMobile ? "0 32px 32px 32px" : "0 64px 64px 64px",
				overflowY: "auto",
				maxHeight: "calc(100vh - 200px)"
			}}>
				<Stack spacing={6}>
				<Stack spacing={3} sx={{ mb: 6 }}>
					<LabeledTextInput
						label={t("myCollection.collectionTitle")}
						placeholder={t("myCollection.collectionTitlePlaceholder")}
						value={title}
						onChange={(e) => setTitle(e.target.value)}
						maxLength={50}
						minLength={6}
						required
					/>
					<LabeledTextarea
						label={t("myCollection.collectionDescription")}
						placeholder={t("myCollection.collectionDescriptionPlaceholder")}
						value={description}
						onChange={(e) => setDescription(e.target.value)}
						maxLength={200}
						minLength={16}
						minRows={3}
						maxRows={5}
						autosize={true}
					/>
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
						<FormControlLabel
							control={
								<Switch
									checked={isPublic}
									onChange={(e) => setIsPublic(e.target.checked)}
									color="primary"
								/>
							}
							label={
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
									{isPublic ? <IconWorld size={20} /> : <IconLock size={20} />}
									<Typography variant="body1" fontWeight={500}>
										{isPublic ? t('myCollection.publicLabel') : t('myCollection.privateLabel')}
									</Typography>
								</Box>
							}
						/>
						<Typography variant="caption" color="text.secondary">
							{isPublic ? t('myCollection.publicSubtitle') : t('myCollection.privateSubtitle')}
						</Typography>
					</Box>
					{isPublic && selectedThreads.length === 0 && (
						<Alert severity="info" sx={{ mb: 2 }}>
							{t('myCollection.publicNeedsAtLeastOne')}
						</Alert>
					)}
					<Box>
						<Typography 
							variant="body1"
							style={{
								fontSize: "16px",
								fontWeight: 500,
								marginBottom: "8px",
								color: "inherit"
							}}
						>
							{t("myCollection.searchThreads")}
						</Typography>
						<TextField
							size="medium"
							placeholder={t("myCollection.searchThreads")}
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<IconSearch size={24} />
									</InputAdornment>
								),
								style: {
									fontSize: "16px",
									height: "48px"
								}
							}}
							fullWidth
							variant="outlined"
						/>
					</Box>
				</Stack>
				<TableContainer 
					component={Paper}
					sx={{
						maxHeight: isMobile ? "40vh" : "50vh",
						minHeight: "200px",
						border: "1px solid rgba(0, 0, 0, 0.12)",
						borderRadius: "8px",
					}}
				>
					<Table stickyHeader size="small">
						<TableHead>
							<TableRow>
								<TableCell padding={isMobile ? "checkbox" : "normal"} sx={{ width: "32px" }}>
									{/* Checkbox column */}
								</TableCell>
								<TableCell 
									padding={isMobile ? "checkbox" : "normal"}
									sx={{ width: "80px", cursor: "pointer" }}
								>
									<TableSortLabel
										active={sortConfig.key === "date"}
										direction={sortConfig.key === "date" ? sortConfig.direction : "asc"}
										onClick={() => handleSort("date")}
									>
										Date
									</TableSortLabel>
								</TableCell>
								<TableCell padding={isMobile ? "checkbox" : "normal"} sx={{ flex: 1 }}>
									<Typography variant="subtitle2">Content</Typography>
								</TableCell>
								{columns.map((col) =>
									!isMobile || !col.mobileHidden ? (
										<TableCell
											key={col.key}
											padding={isMobile ? "checkbox" : "normal"}
											sx={{ width: "96px", cursor: "pointer" }}
										>
											<TableSortLabel
												active={sortConfig.key === col.key}
												direction={sortConfig.key === col.key ? sortConfig.direction : "asc"}
												onClick={() => handleSort(col.key)}
											>
												{col.label}
											</TableSortLabel>
										</TableCell>
									) : null
								)}
							</TableRow>
						</TableHead>
						<TableBody>
							{currentThreads.map((thread) => (
								<Tooltip
									key={thread.id}
									title={
										<Box sx={{ maxWidth: 400, p: 1 }}>
											<Typography variant="body2" sx={{ 
												whiteSpace: "pre-wrap",
												wordBreak: "keep-all",
												lineHeight: 1.5,
												color: "white"
											}}>
												{thread.text.replace(/�/g, "")}
											</Typography>
										</Box>
									}
									followCursor
									slotProps={{
										tooltip: {
											sx: {
												bgcolor: "primary.main",
												"& .MuiTooltip-arrow": {
													color: "primary.main"
												}
											}
										}
									}}
									TransitionProps={{
										timeout: 0
									}}
								>
									<TableRow hover sx={{ height: "48px", cursor: "pointer" }}>
										<TableCell padding={isMobile ? "checkbox" : "normal"} sx={{ width: "32px" }}>
											<Checkbox
												checked={selectedThreads.includes(thread.id)}
												onChange={() => handleThreadSelect(thread.id)}
												color="primary"
											/>
										</TableCell>
										{!isMobile && (
											<TableCell
												padding={isMobile ? "checkbox" : "normal"}
												sx={{ width: "80px", fontSize: "14px" }}
											>
												{new Date(thread.timestamp)
													.toISOString()
													.slice(2, 10)
													.replace(/-/g, "/")}
											</TableCell>
										)}
										<TableCell padding={isMobile ? "checkbox" : "normal"} sx={{ flex: 1 }}>
											<Box
												component="a"
												href={thread.permalink}
												target="_blank"
												rel="noreferrer"
												sx={{
													display: "-webkit-box",
													WebkitLineClamp: 1,
													WebkitBoxOrient: "vertical",
													overflow: "hidden",
													fontSize: "16px",
													fontWeight: "bold",
													textOverflow: "ellipsis",
													textDecoration: "none",
													color: "inherit",
												}}
											>
												{thread.text.split(".")[0].replace(/�/g, "")}
											</Box>
										</TableCell>
										{!isMobile && (
											<>
												<TableCell padding={isMobile ? "checkbox" : "normal"} sx={{ width: "96px" }}>
													{thread.views}
												</TableCell>
												<TableCell padding={isMobile ? "checkbox" : "normal"} sx={{ width: "96px" }}>
													{thread.likes}
												</TableCell>
												<TableCell padding={isMobile ? "checkbox" : "normal"} sx={{ width: "96px" }}>
													{thread.replies}
												</TableCell>
												<TableCell padding={isMobile ? "checkbox" : "normal"} sx={{ width: "96px" }}>
													{thread.reposts}
												</TableCell>
											</>
										)}
									</TableRow>
								</Tooltip>
							))}
						</TableBody>
					</Table>
				</TableContainer>
				<Box sx={{ display: "flex", justifyContent: "center", width: "100%", overflow: "hidden", my: 3 }}>
					<Pagination
						size={isMobile ? "small" : "large"}
						count={Math.ceil(filteredThreads.length / threadsPerPage)}
						siblingCount={isMobile ? 0 : 3}
						page={currentPage}
						onChange={(event, page) => setCurrentPage(page)}
						color="primary"
						shape="rounded"
					/>
				</Box>
				<Stack spacing={3} sx={{ mt: 6 }}>
					<Typography variant="h6">{t("myCollection.selectedThreads")}</Typography>
					<Grid container spacing={2}>
						{collectionList.map((thread) => (
							<Grid size={isMobile ? 6 : 3} key={thread.id}>
								<Box sx={{ position: "relative" }}>
									<IconButton
										sx={{
											position: "absolute",
											top: 8,
											right: 8,
											zIndex: 1,
											backgroundColor: "transparent",
											"&:hover": {
												backgroundColor: "rgba(0, 0, 0, 0.04)",
											},
										}}
										size="small"
										onClick={() => handleRemoveThread(thread.id)}
									>
										<IconX size={16} />
									</IconButton>
									<CardPost
										thread={thread}
										date={thread.timestamp}
										isMobile={isMobile}
										isMetric={isMobile ? false : true}
									/>
								</Box>
							</Grid>
						))}
					</Grid>
				</Stack>
				{isPublic && selectedThreads.length === 0 && (
					<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
						{t('myCollection.helperEmptyCreatePrivate')}
					</Typography>
				)}
				<Button
					size="large"
					variant="contained"
					color="primary"
					endIcon={
						isNew ? <IconPlus size={24} /> : <IconPencilCheck size={24} />
					}
					sx={{
						borderRadius: 3,
						px: 4,
						py: 1.5,
						fontSize: "1.1rem",
					}}
					onClick={() => {
						if (title.length < 6) {
							alert(t("myCollection.titleTooShort"));
							return;
						}
						if (description.length < 16) {
							alert(t("myCollection.descriptionTooShort"));
							return;
						}
						let finalIsPublic = isPublic;
						if (isPublic && selectedThreads.length === 0) {
							const confirmPrivate = window.confirm(t('myCollection.confirmCreateEmptyPublicAsPrivate'));
							if (!confirmPrivate) {
								return;
							}
							finalIsPublic = false;
						}
						if (!isPublic && selectedThreads.length === 0) {
							// 비공개 컬렉션은 스레드 없이도 생성 가능
							console.log("비공개 컬렉션 - 스레드 없이 생성");
						}

						// Admin이 특정 유저를 위해 컬렉션을 생성하는 경우 overrideUserProfile 사용
						const userProfile = overrideUserProfile || user.profile;
						
						const collectionData = {
							title,
							description: description,
							isPublic: finalIsPublic,
							threadList: collectionList,
							userID: userProfile.id,
							name: userProfile.name,
							username: userProfile.username,
							profileImage: userProfile.threads_profile_picture_url,
							createdAt: new Date().toISOString(),
						};
						console.log(selectedThreads);
						console.log(collectionData);
						if (isNew) {
							onUpdateCollection(collectionData);
							alert(t("myCollection.successCreate"));
						} else {
							onUpdateCollection(collectionData);
							alert(t("myCollection.successUpdate"));
						}
						setModalOpen(false);
					}}
				>
					{isNew ? t("myCollection.create") : t("myCollection.update")}
				</Button>
			</Stack>
			</DialogContent>
		</Dialog>
	);
};

export default UpdateCollectionModal;
